/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
  readonly onOffsetChange: (offset: number) => void;
}

/** Offset/limit pager — prev/next + "page X of Y" (P1-1). Hidden for a single page. */
export function Pagination({
  total,
  limit,
  offset,
  onOffsetChange,
}: PaginationProps): JSX.Element | null {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;
  const prev = useCallback(() => {
    onOffsetChange(Math.max(0, offset - limit));
  }, [offset, limit, onOffsetChange]);
  const next = useCallback(() => {
    onOffsetChange(offset + limit);
  }, [offset, limit, onOffsetChange]);

  if (total <= limit) {
    return null;
  }

  return (
    <div className="mt-4 flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
      <span>{t('pagination.pageOf', { page, total: pageCount })}</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={prev}
          disabled={page <= 1}
          aria-label={t('pagination.prev')}
          className="focus-ring inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 font-medium disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {t('pagination.prev')}
        </button>
        <button
          type="button"
          onClick={next}
          disabled={page >= pageCount}
          aria-label={t('pagination.next')}
          className="focus-ring inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 font-medium disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
        >
          {t('pagination.next')}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
